// Stock counts (migrations 070, 071; Mitch, 2026-11-04): the day's short list of SKUs to count by hand, for the server
// (which makes it once a day) and the screens (which say why each is on it). At most 4 SKUs or about 250 units a day,
// never a SKU with more on hand than that ("If a sku has thousands in stock, probably not worth our time to count it")
// and never one at zero or below ("that makes no sense"), ranked so what matters most comes first:
// - Due: never counted; the top sellers (the SKUs making 80% of the last 90 days' units) after about 6 months; the rest
//   after about a year; or running low (it sells, and what's left is about three weeks of sales or less: quick to count,
//   and right before a reorder, when the number matters most), unless counted in the last 60 days.
// - Ranked by sales: score = (units sold in 90 days + 1) × how overdue (never counted 1, else days since ÷ its interval),
//   doubled when running low. So the best sellers come first, a slow seller's turn comes when it's long overdue.
// Nothing counted in the last 30 days comes back. The day fills in rank order with what fits the units left.

export const COUNT_REASONS = ["running_low", "never", "due", "picked"] as const;
export type CountReason = (typeof COUNT_REASONS)[number];
export const COUNT_REASON_LABELS: Record<CountReason, string> = {
  running_low: "Running low",
  never: "Never counted",
  due: "Due",
  picked: "Picked",
};

/** The rules' numbers, here so the screens can say them. */
export const COUNT_RULES = {
  topShare: 0.8, // the top sellers: the SKUs making this share of the last 90 days' units
  topEveryDays: 182,
  restEveryDays: 365,
  minGapDays: 30, // nothing counted this recently comes back
  lowGapDays: 60, // running low again counts only after this long
  lowDaysOfSales: 21, // running low: this many days of sales or less left
  lowFloor: 5, // … or this many units or fewer
  lowBoost: 2,
} as const;

export type CountCandidate = {
  itemId: string;
  locationId: string;
  sku: string;
  onHand: number;
  available: number;
  /** Units sold in the last 90 days (0 for a part). */
  units90: number;
  /** The day it was last counted there, if ever. */
  lastCounted: string | null;
};

export type CountLimits = { unitsPerDay: number; skusPerDay: number; skipOver: number };

export type CountPick = {
  itemId: string;
  locationId: string;
  reason: CountReason;
  onHand: number;
  units90: number;
  lastCounted: string | null;
  top: boolean;
  score: number;
};

const days = (from: string, to: string) => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
const keyOf = (c: { itemId: string; locationId: string }) => `${c.itemId}|${c.locationId}`;

/** The SKUs making `topShare` of the units sold (by item: one location's or several, its sales are the item's). */
export function topSellers(cands: readonly Pick<CountCandidate, "itemId" | "units90">[]): Set<string> {
  const byItem = new Map<string, number>();
  for (const c of cands) if (c.units90 > 0) byItem.set(c.itemId, c.units90);
  const total = [...byItem.values()].reduce((n, u) => n + u, 0);
  const out = new Set<string>();
  let before = 0;
  for (const [id, u] of [...byItem].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))) {
    if (before >= total * COUNT_RULES.topShare) break;
    out.add(id);
    before += u;
  }
  return out;
}

/** Whether a candidate is due today, why, and its score (higher first), or null when it isn't. */
export function dueToday(c: CountCandidate, today: string, top: boolean, limits: CountLimits): { reason: CountReason; score: number } | null {
  if (c.onHand <= 0 || c.available <= 0 || c.onHand > limits.skipOver) return null;
  const since = c.lastCounted ? days(c.lastCounted, today) : null;
  if (since !== null && since < COUNT_RULES.minGapDays) return null;
  const every = top ? COUNT_RULES.topEveryDays : COUNT_RULES.restEveryDays;
  const low =
    c.units90 > 0 &&
    c.onHand <= Math.max(COUNT_RULES.lowFloor, (c.units90 / 90) * COUNT_RULES.lowDaysOfSales) &&
    (since === null || since >= COUNT_RULES.lowGapDays);
  const overdue = since === null ? 1 : since / every;
  if (!low && overdue < 1) return null;
  const score = (c.units90 + 1) * Math.max(overdue, low ? 1 : 0) * (low ? COUNT_RULES.lowBoost : 1);
  return { reason: low ? "running_low" : since === null ? "never" : "due", score };
}

/** Every due candidate, best first. */
export function rankCounts(cands: readonly CountCandidate[], today: string, limits: CountLimits): CountPick[] {
  const top = topSellers(cands);
  return cands
    .flatMap((c) => {
      const d = dueToday(c, today, top.has(c.itemId), limits);
      return d
        ? [
            {
              itemId: c.itemId,
              locationId: c.locationId,
              reason: d.reason,
              onHand: c.onHand,
              units90: c.units90,
              lastCounted: c.lastCounted,
              top: top.has(c.itemId),
              score: d.score,
              sku: c.sku,
            },
          ]
        : [];
    })
    .sort((a, b) => b.score - a.score || b.units90 - a.units90 || a.sku.localeCompare(b.sku))
    .map(({ sku: _, ...p }) => p);
}

/**
 * The day's list (or what's left of it): the best ranked, leaving out `exclude` (already on today's list, or taken off
 * it), each that fits the units left, up to the SKUs left.
 */
export function pickCounts(
  cands: readonly CountCandidate[],
  today: string,
  limits: CountLimits,
  opts: { exclude?: ReadonlySet<string>; units?: number; skus?: number } = {},
): CountPick[] {
  const out: CountPick[] = [];
  let units = opts.units ?? 0;
  let skus = opts.skus ?? 0;
  for (const p of rankCounts(cands, today, limits)) {
    if (skus >= limits.skusPerDay || units >= limits.unitsPerDay) break;
    if (opts.exclude?.has(keyOf(p)) || units + p.onHand > limits.unitsPerDay) continue;
    units += p.onHand;
    skus++;
    out.push(p);
  }
  return out;
}

/** Why a SKU is on the list, in words (no stock numbers: whoever counts never sees the books'). */
export function countWhy(
  t: { reason: string; units_90: number | string; top: boolean; last_counted: string | null },
  opts: { pickedBy?: string; day?: (iso: string) => string } = {},
): string {
  const { pickedBy, day = (d: string) => d } = opts;
  const sold = Number(t.units_90);
  const sales = sold > 0 ? `${t.top ? "Top seller: " : ""}${sold.toLocaleString("en-US")} sold in the last 90 days` : "No sales in the last 90 days";
  const last = t.last_counted ? `last counted ${day(t.last_counted)}` : "never counted";
  if (t.reason === "picked") return `Picked${pickedBy ? ` by ${pickedBy}` : ""} to count today`;
  if (t.reason === "running_low") return `${sales} · running low, so it's quick to count before it's reordered · ${last}`;
  return `${sales} · ${last}`;
}
